import type { SparklineBar, Verdict } from './presentation';
import { VERDICT_BANDS, VERDICT_LABELS } from './presentation';

type ValueCellProps = {
  valueLabel: string;
  verdict: Verdict | null;
  bars: SparklineBar[];
  width: number;
  shortName: string;
  // Pins the cell alongside the rank and team columns from lg up.
  stickyOffset: number;
};

// The sparkline shows the first few gameweeks of the window at a readable
// size; a longer window is summarised with a "+N" rather than squeezed in, as
// the cells to the right already carry every fixture.
const SPARKLINE_MAX_BARS = 8;
const SPARKLINE_WIDTH = 84;
const SPARKLINE_HEIGHT = 22;
const MIN_BAR_HEIGHT = 3;
const EMPTY_BAR_COLOUR = '#EFEFEF';
const NEUTRAL_VALUE_COLOUR = '#5C5C5D';

export const ValueCell = ({
  valueLabel,
  verdict,
  bars,
  width,
  shortName,
  stickyOffset
}: ValueCellProps) => {
  const visibleBars = bars.slice(0, SPARKLINE_MAX_BARS);
  const hiddenBarCount = bars.length - visibleBars.length;

  return (
    <div
      className="z-0 flex min-h-[52px] items-center gap-3 border-x border-neutral-200 bg-[#FBFBFC] px-4 lg:sticky lg:z-10"
      style={{ width, left: stickyOffset }}
    >
      <span
        data-testid={`fixture-summary-value-${shortName}`}
        className="min-w-[44px] text-[22px] font-bold leading-none tabular-nums"
        style={{
          color: verdict ? VERDICT_BANDS[verdict].text : NEUTRAL_VALUE_COLOUR
        }}
      >
        {valueLabel}
      </span>
      <span className="flex flex-col gap-1">
        <span
          aria-hidden="true"
          className="flex items-end gap-px"
          style={{ width: SPARKLINE_WIDTH, height: SPARKLINE_HEIGHT }}
        >
          {visibleBars.map((bar) => (
            <span
              key={bar.gameweek}
              className="min-w-0 flex-1 rounded-[1px]"
              style={{
                height:
                  bar.ease === null
                    ? MIN_BAR_HEIGHT
                    : Math.max(
                        MIN_BAR_HEIGHT,
                        Math.round(bar.ease * SPARKLINE_HEIGHT)
                      ),
                backgroundColor: bar.band?.bar ?? EMPTY_BAR_COLOUR
              }}
            />
          ))}
          {hiddenBarCount > 0 && (
            <span className="ml-1 self-center font-open-sans text-[9px] leading-none text-neutral-500">
              +{hiddenBarCount}
            </span>
          )}
        </span>
        {verdict && (
          <span className="font-open-sans text-[11px] leading-none text-neutral-600">
            {VERDICT_LABELS[verdict]}
          </span>
        )}
      </span>
    </div>
  );
};
